'use client';

import { notFoundPathName, selectedLocaleLocalStorageKey } from '@/constants';
import { locales } from '@/i18n/locale-config';
import { usePathname, useRouter } from '@/i18n/navigation';
import { useClickOutside } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import { Globe } from 'lucide-react';
import { useLocale } from 'next-intl';
import { useParams } from 'next/navigation';
import { useEffect, useState, useTransition } from 'react';

export default function LocaleSwitcher({ className }: { className?: string }) {
    const router = useRouter();
    const pathname = usePathname();
    const params = useParams();
    const currentLocale = useLocale();
    const selectorRef = useClickOutside<HTMLDivElement>(() => {
        setIsOpen(false);
    });

    const [isOpen, setIsOpen] = useState(false);
    const [isPending, startTransition] = useTransition();

    function onSelectChange(nextLocale: string) {
        startTransition(() => {
            // @ts-expect-error -- TypeScript will validate that only known `params`
            // are used in combination with a given `pathname`. Since the two will
            // always match for the current route, we can skip runtime checks.
            router.replace({ pathname, params }, { locale: nextLocale });
        });
    }

    useEffect(() => {
        if (pathname !== notFoundPathName)
            localStorage.setItem(selectedLocaleLocalStorageKey, currentLocale);
    }, [currentLocale, pathname]);

    return (
        <div
            className={cn(
                'relative inline-block text-left',
                isPending && 'transition-opacity [&:disabled]:opacity-30',
                className
            )}
        >
            <button
                type="button"
                aria-label="Language"
                aria-expanded={isOpen}
                className="flex size-9 items-center justify-center border-none bg-none max-2xl:size-11 max-2xl:rounded-sm hover:max-2xl:bg-white/10"
                onClick={() => setIsOpen(!isOpen)}
            >
                <Globe className="size-[15px] text-white max-2xl:size-5" />
            </button>

            {isOpen && (
                <div
                    className="absolute left-0 mt-2 w-fit origin-top-left bg-white px-2 py-1 opacity-90 shadow-m max-2xl:px-3 max-2xl:py-2"
                    ref={selectorRef}
                >
                    <div
                        className="flex flex-col gap-0.5 py-1 max-2xl:gap-1"
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="options-menu"
                    >
                        {locales.map((locale) => {
                            const isCurrentLocale = locale === currentLocale;

                            return (
                                <button
                                    type="button"
                                    className={cn(
                                        'group text-sm font-medium capitalize hover:text-brand max-sm:p-1 max-2xl:px-2 max-2xl:py-1.5 max-2xl:text-base',
                                        isCurrentLocale && 'text-brand',
                                    )}
                                    onClick={() => onSelectChange(locale)}
                                    key={locale}
                                >
                                    {locale}
                                    <span
                                        className={cn(
                                            'block h-[1px] max-w-0 -translate-y-1 bg-brand transition-all duration-300 group-hover:max-w-full',
                                            isCurrentLocale && 'max-w-full',
                                        )}
                                    ></span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
